"use client";

import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { ProductData } from "@/types/product";

interface ProductsListingClientProps {
  products: ProductData[];
  categories: string[];
  currentCategory: string;
  currentSort: string;
  currentPage: number;
  totalPages: number;
  totalProducts: number;
}

export default function ProductsListingClient({
  products,
  categories,
  currentCategory,
  currentSort,
  currentPage,
  totalPages,
  totalProducts,
}: ProductsListingClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const sortOptions = [
    { value: "latest", label: "Latest" },
    { value: "price-low", label: "Price: Low to High" },
    { value: "price-high", label: "Price: High to Low" },
    { value: "name", label: "Name A-Z" },
  ];

  const updateURL = (
    newCategory?: string,
    newSort?: string,
    newPage?: number
  ) => {
    const params = new URLSearchParams(searchParams);

    if (newCategory !== undefined) {
      if (newCategory === "all") {
        params.delete("category");
      } else {
        params.set("category", newCategory);
      }
    }

    if (newSort !== undefined) {
      if (newSort === "latest") {
        params.delete("sort");
      } else {
        params.set("sort", newSort);
      }
    }

    if (newPage !== undefined) {
      if (newPage === 1) {
        params.delete("page");
      } else {
        params.set("page", newPage.toString());
      }
    }

    const queryString = params.toString();
    const newURL = queryString ? `/products?${queryString}` : "/products";
    router.push(newURL);
  };

  const handleCategoryChange = (category: string) => {
    updateURL(category, currentSort, 1);
  };

  const handleSortChange = (sortBy: string) => {
    updateURL(currentCategory, sortBy, 1);
  };

  const handlePageChange = (page: number) => {
    updateURL(currentCategory, currentSort, page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="bg-white py-8">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8">
        {/* Products Heading */}
        <h2 className="text-3xl font-bold text-gray-900 mb-4 font-[family-name:var(--font-playfair)]">
          Products
        </h2>

        {/* Horizontal Line */}
        <div className="border-b border-gray-200 mb-8"></div>

        {/* Pill-style Category Filters */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Filter by Product
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleCategoryChange("all")}
              className={`px-6 py-3 rounded-xl text-sm font-medium transition-colors ${
                currentCategory === "all"
                  ? "bg-[#7771B8] text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Products
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-colors ${
                  currentCategory === category
                    ? "bg-[#7771B8] text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Sort and Results */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div className="text-gray-600">
            Showing {products.length} of {totalProducts} products
            {currentCategory !== "all" && (
              <span className="ml-2 text-sm">
                filtered by &quot;{currentCategory}&quot;
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-700">Sort by:</span>
            <select
              value={currentSort}
              onChange={(e) => handleSortChange(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-medium hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7771B8] focus:border-transparent cursor-pointer"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Product Grid */}
        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {products.map((product) => {
              /**
               * The lowest price actually set for this product.
               *
               * A £0 amount means "not priced yet" in this catalogue rather
               * than free, the same rule the repeat order page applies when it
               * builds its list. Excluding them stops an unpriced strength
               * becoming a "Starting from £0" headline, and stops it being
               * ordered for nothing.
               */
              const pricedAmounts = product.productPrices.list
                .map((option) => Number(option.amount))
                .filter((amount) => Number.isFinite(amount) && amount > 0);
              const startingPrice = pricedAmounts.length
                ? Math.min(...pricedAmounts)
                : null;

              /**
               * No price means nothing to sell. The consultation is closed off
               * rather than left open, since starting one for a product with no
               * price ends either at £0 or at a dead end.
               */
              const isAvailable = startingPrice !== null;

              return (
              <div
                key={product.productId}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col h-full"
              >
                {/* Product Image */}
                <div className="aspect-square relative overflow-hidden">
                  {product.productBase.leadImage ? (
                    <Image
                      src={product.productBase.leadImage}
                      alt={product.productName}
                      fill
                      className="object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="bg-[#7771B8] text-white text-xs font-bold py-2 px-4 mb-4 inline-block">
                          UKWEIGHTLOSS
                        </div>
                        <div className="text-lg font-bold text-gray-900 mb-2">
                          Health Product
                        </div>
                        <div className="text-sm text-gray-500">
                          Professional Healthcare
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 line-clamp-2">
                    {product.productName}
                  </h3>

                  {/*
                    Only the starting price is shown here.

                    The per-dose price list that used to sit on each card was
                    removed for compliance: these are prescription-only
                    medicines, and listing each strength against its price on a
                    public browse page reads as promotion rather than
                    information. The doses are still on the product page, which
                    is where someone has chosen to look at that treatment.
                  */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      {isAvailable ? (
                        <>
                          <div className="text-sm text-gray-500">
                            Starting from
                          </div>
                          <span className="text-2xl font-bold text-[#7771B8]">
                            £{startingPrice}
                          </span>
                        </>
                      ) : (
                        <div className="text-sm font-medium text-gray-500">
                          Currently unavailable
                        </div>
                      )}
                    </div>
                  </div>

                  {/*
                    View details is the only action on a card.

                    Starting a consultation is a decision to take on the
                    treatment's own page, where the doses, the suitability
                    criteria and the important information are in front of the
                    patient. Offering it from a browse grid put the shortest
                    path to a prescription-only medicine next to the least
                    context.

                    It also settles the layout: every card now carries the same
                    single action, so cards no longer differ depending on
                    whether a product happens to have a form and a price.

                    mt-auto rather than a fixed height, so titles that wrap to
                    two lines still leave every card's button aligned.
                  */}
                  <div className="mt-auto">
                    <Link
                      href={`/products/${product.slug || product.productId}`}
                      className="block w-full bg-white text-[#7771B8] py-3.5 px-6 rounded-xl text-center font-semibold border-2 border-[#7771B8] hover:bg-[#7771B8] hover:text-white hover:shadow-md transition-all duration-200"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No products found.</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 hover:border-gray-400 transition-colors"
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 border rounded-lg font-medium transition-colors ${
                    currentPage === page
                      ? "bg-[#7771B8] text-white border-[#7771B8]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:border-gray-400"
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 hover:border-gray-400 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
